import { useCallback, useEffect, useRef, useState } from "react";
import type { ProductId } from "@KassaroAB/shared/types/ids";
import type { RestProduct } from "@KassaroAB/shared/types/product";
import { getProductsPageApi } from "../api/productsApi";
import {
  productStoreCacheId,
  type CatalogScope,
} from "../api/catalogScope";
import { ProductStore } from "../data/productStore";

const SYNC_PAGE_SIZE = 10000;

export type ProductStoreStatus = "idle" | "syncing" | "ready" | "error";

export type ProductSyncPhase = "full" | "incremental";

export interface ProductSyncProgress {
  phase: ProductSyncPhase;
  fetchedCount: number;
}

interface SyncStoreOptions {
  onProgress?: (progress: ProductSyncProgress) => void;
  onPageStored?: () => void;
}

const syncStore = async (
  store: ProductStore,
  scope: CatalogScope,
  updatedSince: string | null,
  options?: SyncStoreOptions,
): Promise<number> => {
  let cursor: ProductId | undefined;
  let fetchedCount = 0;
  const phase: ProductSyncPhase = updatedSince === null ? "full" : "incremental";

  while (true) {
    const page = await getProductsPageApi(scope, {
      updatedSince: updatedSince ?? undefined,
      cursor,
      limit: SYNC_PAGE_SIZE,
    });

    if (page.length === 0) {
      break;
    }

    fetchedCount += page.length;
    options?.onProgress?.({ phase, fetchedCount });

    const toUpsert = page.filter((product) => !product.isDeleted);
    const toDelete = page
      .filter((product) => product.isDeleted)
      .map((product) => product.id);

    if (toUpsert.length > 0) {
      await store.upsertMany(toUpsert);
    }
    if (toDelete.length > 0) {
      await store.deleteMany(toDelete);
    }

    options?.onPageStored?.();

    if (page.length < SYNC_PAGE_SIZE) {
      break;
    }

    const lastId = page[page.length - 1]?.id;
    if (!lastId || lastId === cursor) {
      break;
    }
    cursor = lastId;
  }

  return fetchedCount;
};

export const useProductStore = (scope: CatalogScope | null) => {
  const storeRef = useRef<ProductStore | null>(null);
  const [statusMap, setStatusMap] = useState<
    Record<string, ProductStoreStatus>
  >({});
  const [version, setVersion] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncProgress, setSyncProgress] = useState<ProductSyncProgress | null>(
    null,
  );

  const cacheId = scope ? productStoreCacheId(scope) : null;

  const status: ProductStoreStatus = cacheId
    ? (statusMap[cacheId] ?? "syncing")
    : "idle";

  const setStoreStatus = useCallback(
    (id: string, value: ProductStoreStatus) => {
      setStatusMap((previous) => ({ ...previous, [id]: value }));
    },
    [],
  );

  const runSync = useCallback(
    async (
      store: ProductStore,
      currentScope: CatalogScope,
      updatedSince: string | null,
    ): Promise<void> => {
      const id = productStoreCacheId(currentScope);
      setSyncProgress({
        phase: updatedSince === null ? "full" : "incremental",
        fetchedCount: 0,
      });
      await syncStore(store, currentScope, updatedSince, {
        onProgress: setSyncProgress,
        onPageStored: () => setVersion((value) => value + 1),
      });
      setSyncProgress(null);
      setStoreStatus(id, "ready");
      setVersion((value) => value + 1);
      setLastSyncedAt(new Date());
    },
    [setStoreStatus],
  );

  useEffect(() => {
    if (!scope || !cacheId) {
      return;
    }

    let cancelled = false;
    const store = new ProductStore();
    storeRef.current = store;

    store
      .init(cacheId)
      .then(async (meta) => {
        if (cancelled) {
          return;
        }
        const updatedSince = meta.count === 0 ? null : meta.maxUpdatedAt;
        await runSync(store, scope, updatedSince);
      })
      .catch((error) => {
        console.error("[useProductStore] initial sync failed", error);
        if (!cancelled) {
          setSyncProgress(null);
          setStoreStatus(cacheId, "error");
        }
      });

    return () => {
      cancelled = true;
      store.terminate();
      storeRef.current = null;
    };
  }, [scope, cacheId, setStoreStatus, runSync]);

  const resync = useCallback(async () => {
    const store = storeRef.current;
    if (!store || !scope || !cacheId) {
      return;
    }
    setStoreStatus(cacheId, "syncing");
    await store
      .meta()
      .then((meta) => runSync(store, scope, meta.maxUpdatedAt))
      .catch((error) => {
        console.error("[useProductStore] resync failed", error);
        setSyncProgress(null);
        setStoreStatus(cacheId, "error");
      });
  }, [scope, cacheId, setStoreStatus, runSync]);

  const forceResync = useCallback(async () => {
    const store = storeRef.current;
    if (!store || !scope || !cacheId) {
      return;
    }
    setStoreStatus(cacheId, "syncing");
    await store
      .clear()
      .then(() => runSync(store, scope, null))
      .catch((error) => {
        console.error("[useProductStore] force resync failed", error);
        setSyncProgress(null);
        setStoreStatus(cacheId, "error");
      });
  }, [scope, cacheId, setStoreStatus, runSync]);

  const applyUpdated = useCallback(async (products: RestProduct[]) => {
    const store = storeRef.current;
    if (!store || products.length === 0) {
      return;
    }
    const toUpsert = products.filter((product) => !product.isDeleted);
    const toDelete = products
      .filter((product) => product.isDeleted)
      .map((product) => product.id);
    if (toUpsert.length > 0) {
      await store.upsertMany(toUpsert);
    }
    if (toDelete.length > 0) {
      await store.deleteMany(toDelete);
    }
    setVersion((value) => value + 1);
  }, []);

  return {
    store: storeRef,
    status,
    version,
    lastSyncedAt,
    syncProgress,
    resync,
    forceResync,
    applyUpdated,
  };
};
